import { z } from "zod";
import { useTRPC } from "../../trpc";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo } from "react";
import { RelativeLink } from "../../components/Link";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useNavigate } from "@tanstack/react-router";
import { IconAt, IconExclamationCircle, IconLock } from "@tabler/icons-react";
import { PasswordStrength } from "../../components/PasswordStrength";
import { FormattedMessage, useIntl } from "react-intl";
import { Alert, Anchor, Button, Checkbox, Group, PasswordInput, Stack, TextInput, Title } from "@mantine/core";
import { emailSchema, nameSchema, passwordSchemaStrength } from "../../utils/schema";
import { getInitialLocale, type Locale } from "@project/common";
import { captureEvent } from "../../utils/posthog";
import { useSetUser } from "../../hooks/user";
import { useMutation } from "@tanstack/react-query";
import { Event } from "../../constants/events";
import { Divider } from "@mantine/core";
import { GithubButton, GoogleButton } from "./components/SocialLogins";
import { TermsConditionsCheckbox } from "./components/TermsConditionsCheckbox";

const defaultFormValues = {
  email: "",
  name: "",
  password: "",
  organization: "",
  marketingConsent: false,
  termsAccepted: false,
  locale: getInitialLocale(),
};

export function RegisterUser() {
  const intl = useIntl();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const setUser = useSetUser();

  const { mutate, isPending, error } = useMutation(trpc.registerUserAccount.mutationOptions({
    onSuccess: async (user) => {
      setUser(user);
      await navigate({
        to: "/sign-in",
        search: {
          shouldVerifyEmail: true,
        },
      });
    },
  }));

  const validate = useMemo(() => zod4Resolver(
    z.object({
      email: emailSchema(intl),
      name: nameSchema(intl),
      password: passwordSchemaStrength(intl),
      locale: z.string(),
      marketingConsent: z.boolean(),
      termsAccepted: z.boolean().refine(val => val, {
        message: intl.formatMessage({
          id: "error.termsRequired",
          defaultMessage: "You must accept the Terms of Service and Privacy Policy",
        }),
      }),
    }),
  ), [intl]);

  const { getInputProps, onSubmit, setFieldValue, values } = useForm({
    validateInputOnBlur: true,
    initialValues: defaultFormValues,
    validate,
  });

  useEffect(() => {
    setFieldValue("locale", intl.locale as Locale);
  }, [setFieldValue, intl]);

  const handleSubmit = useCallback((form: typeof defaultFormValues) => {
    captureEvent(Event.AuthRegisteredAccount, {
      email: form.email,
      provider: "email",
    });
    mutate({
      ...form,
      organization: form.name,
    });
  }, [mutate]);

  let errorMessage: ReactNode = null;
  if (error) {
    if (error.message.includes("Email is disposable")) {
      errorMessage = (
        <FormattedMessage
          id="error.disposableEmail"
          defaultMessage="Disposable email addresses are not allowed"
        />
      );
    }
    else {
      errorMessage = error.message;
    }
  }

  return (
    <Stack>
      <Stack gap={12}>
        <Anchor
          href="/api/login/google"
          w="100%"
          underline="never"
          onClick={() => {
            captureEvent(Event.AuthRegisteredAccount, {
              provider: "google",
            });
          }}
        >
          <GoogleButton variant="default" fullWidth>
            <FormattedMessage
              id="button.continueWithGoogle"
              defaultMessage="Continue with Google"
            />
          </GoogleButton>
        </Anchor>
        <Anchor
          href="/api/login/github"
          w="100%"
          underline="never"
          onClick={() => {
            captureEvent(Event.AuthRegisteredAccount, {
              provider: "github",
            });
          }}
        >
          <GithubButton variant="default" fullWidth>
            <FormattedMessage
              id="button.continueWithGithub"
              defaultMessage="Continue with Github"
            />
          </GithubButton>
        </Anchor>
      </Stack>
      <Divider label={(
        <FormattedMessage
          id="divider.or"
          defaultMessage="OR"
        />
      )}
      />

      <form onSubmit={onSubmit(handleSubmit)}>
        <Stack gap="xs">
          <Title size="h3">
            <FormattedMessage id="title.createYourAccount" defaultMessage="Create your account" />
          </Title>
          {error && (
            <Alert variant="light" color="red" icon={<IconExclamationCircle />}>
              {errorMessage}
            </Alert>
          )}
          <TextInput
            required
            leftSection={<IconAt size={16} />}
            autoComplete="email"
            label={<FormattedMessage id="label.email" defaultMessage="Email" />}
            {...getInputProps("email")}
          />
          <TextInput
            required
            autoComplete="name"
            label={<FormattedMessage id="label.name" defaultMessage="Name" />}
            {...getInputProps("name")}
          />
          <PasswordInput
            required
            leftSection={<IconLock size={16} />}
            autoComplete="new-password"
            label={<FormattedMessage id="label.password" defaultMessage="Password" />}
            {...getInputProps("password")}
          />
          <PasswordStrength {...values} />

          <TermsConditionsCheckbox
            locale={values.locale}
            {...getInputProps("termsAccepted", { type: "checkbox" })}
          />

          <Checkbox
            pt="xs"
            label={<FormattedMessage id="label.marketingConsent" defaultMessage="I would like to receive occasional email updates and special offers for products, services, and events." />}
            {...getInputProps("marketingConsent", { type: "checkbox" })}
          />

          <Stack gap="xs" mt="xs">
            <Button type="submit" fullWidth loading={isPending} variant="primary">
              <FormattedMessage id="button.register" defaultMessage="Register" />
            </Button>
            <Group gap="xs">
              <FormattedMessage id="register.alreadyHaveAccount" defaultMessage="Already have an account?" />
              <RelativeLink underline="always" href="../sign-in">
                <FormattedMessage id="link.signIn" defaultMessage="Sign in" />
              </RelativeLink>
            </Group>
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
}
